import type { PortfolioBenchmarkPolicy } from "../portfolio-performance.js";
import { PORTFOLIO_ASSET_CLASSES_V2, type DecimalValue, type EvidenceReferenceV2, type IsoDateTime, type PortfolioSupportStateV2 } from "./primitives.js";
import type { PlannedExitRuleV2, TradeHorizonV2 } from "./trading.js";

export const PORTFOLIO_POLICY_MAXIMUM_PLANNED_EXITS_V2 = 20;
export const PORTFOLIO_POLICY_DEFINITION_VERSION_V2 = "portfolio-policy-definition.v2.2" as const;
export const PORTFOLIO_POLICY_ASSET_CLASSES_V2 = [...PORTFOLIO_ASSET_CLASSES_V2, "CASH"] as const;
export type PortfolioPolicyAssetClassV2 = typeof PORTFOLIO_POLICY_ASSET_CLASSES_V2[number];

export interface PortfolioPolicyPlannedCapitalFlowV2 {
  sequence: number;
  direction: "INFLOW" | "OUTFLOW";
  amount: DecimalValue;
  currency: string;
  /** ISO calendar date (YYYY-MM-DD), not a trusted execution timestamp. */
  plannedFor: string;
  note: string | null;
}

export interface PortfolioPolicyHorizonAllocationV2 {
  horizon: TradeHorizonV2;
  enabled: boolean;
  targetAllocationPct: DecimalValue;
}

export interface PortfolioPolicyHorizonPolicyV2 {
  defaultHorizon: TradeHorizonV2;
  allocations: PortfolioPolicyHorizonAllocationV2[];
}

export interface PortfolioPolicyAssetAllocationBandV2 {
  assetClass: PortfolioPolicyAssetClassV2;
  enabled: boolean;
  minimumAllocationPct: DecimalValue;
  targetAllocationPct: DecimalValue;
  maximumAllocationPct: DecimalValue;
}

export interface PortfolioPolicyAssetAllocationV2 {
  bands: PortfolioPolicyAssetAllocationBandV2[];
}

export interface PortfolioPolicyAssetClassSupportV2 {
  assetClass: PortfolioPolicyAssetClassV2;
  supportState: PortfolioSupportStateV2;
}

/** Constraints only; PlannedExitRuleV2 instances belong to Trade Plans. */
export interface PortfolioPolicyExitConstraintsV2 {
  allowedQuantityModes: PlannedExitRuleV2["quantityMode"][];
  allowedTriggerTypes: PlannedExitRuleV2["triggerType"][];
  allowedPostExecutionActions: PlannedExitRuleV2["postExecutionAction"][];
  maximumPlannedExits: number;
  requirePreEntryExitPlan: boolean;
  requireInitialStop: boolean;
  allowStopWideningAfterEntry: boolean;
}

/** Existing benchmark policy, frozen inside the immutable Active policy version. */
export type PortfolioPolicyBenchmarkPolicyV2 = PortfolioBenchmarkPolicy;

export const PORTFOLIO_POLICY_FEE_ASSUMPTION_STATES_V2 = [
  "KNOWN_SCHEDULE",
  "BROKER_REPORTED",
  "UNKNOWN",
] as const;
export type PortfolioPolicyFeeAssumptionStateV2 = typeof PORTFOLIO_POLICY_FEE_ASSUMPTION_STATES_V2[number];

/** Owner selection only. feeScheduleRef is always resolved by the server. */
export interface PortfolioPolicySourceCostSelectionV2 {
  brokerSourceId: string | null;
  feeAssumptionState: PortfolioPolicyFeeAssumptionStateV2;
}

/** Immutable activation evidence; never a copy of per-fill ExecutionCostsV2. */
export interface PortfolioPolicySourceCostSnapshotV2 {
  brokerSourceId: string | null;
  feeScheduleRef: string | null;
  feeEvidenceState: PortfolioPolicyFeeAssumptionStateV2;
}

/** Client input. The server owns acknowledgedAt. */
export interface PortfolioPolicyWarningAcknowledgementInputV2 {
  issueCode: string;
  note: string | null;
}

export interface PortfolioPolicyWarningAcknowledgementV2 extends PortfolioPolicyWarningAcknowledgementInputV2 {
  acknowledgedAt: IsoDateTime;
}

/** REVIEWED/ACTIVE metadata, intentionally excluded from the Draft fingerprint. */
export interface PortfolioPolicyReviewGovernanceV2 {
  reviewedAt: IsoDateTime;
  reviewEvidenceRefs: EvidenceReferenceV2[];
  warningAcknowledgements: PortfolioPolicyWarningAcknowledgementV2[];
}

export interface PortfolioPolicyRevocationV2 {
  revokedAt: IsoDateTime;
  ownerDecisionId: string;
  reason: string;
}
